import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { Check, X, Truck, PencilSimple, Trash } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useFleetOptions, buildVehiculoHandler, buildConductorHandler } from '@/hooks/useFleetOptions';
import { MovilizacionInfoGrid } from './MovilizacionInfoGrid';
import { AsignacionDisplay } from './AsignacionDisplay';
import { FleetSelectFields } from './FleetSelectFields';
import { MovilizacionStatusHeader } from './MovilizacionStatusHeader';
import { CierreButtons, CierreDialog } from './CierreDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getActionColor = (action) => {
  if (action === 'en_espera_asignacion') return '#0891B2';
  if (action === 'rechazado') return '#FF3B30';
  return '#F5A623';
};

const AdminActionButtons = ({ movilizacion, onOpen, onOpenAsignacion, onDelete }) => (
  <div className="flex gap-2 flex-wrap justify-end">
    {movilizacion.estado === 'agendado' && (
      <>
        <Button
          data-testid="aprobar-button"
          onClick={() => onOpen('en_espera_asignacion')}
          size="sm"
          className="gap-2 bg-[#0891B2] hover:bg-[#0e7490] text-white font-['IBM_Plex_Sans'] transition-colors"
        >
          <Check size={16} weight="bold" /> Aprobar
        </Button>
        <Button
          data-testid="rechazar-button"
          onClick={() => onOpen('rechazado')}
          size="sm"
          variant="outline"
          className="gap-2 border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white font-['IBM_Plex_Sans'] transition-colors"
        >
          <X size={16} weight="bold" /> Rechazar
        </Button>
      </>
    )}
    {movilizacion.estado === 'en_espera_asignacion' && (
      <Button
        data-testid="asignar-recursos-button"
        onClick={onOpenAsignacion}
        size="sm"
        className="gap-2 bg-[#008630] hover:bg-[#006624] text-white font-['IBM_Plex_Sans'] transition-colors"
      >
        <Truck size={16} weight="bold" /> Asignar Recursos
      </Button>
    )}
    {movilizacion.estado === 'asignado' && (
      <>
        <Button
          data-testid="editar-asignacion-admin-button"
          onClick={onOpenAsignacion}
          size="sm"
          variant="outline"
          className="gap-2 border-[#008630] text-[#008630] hover:bg-[#008630] hover:text-white font-['IBM_Plex_Sans'] transition-colors"
        >
          <PencilSimple size={16} weight="bold" /> Editar Asignación
        </Button>
      </>
    )}
    <Button
      data-testid="eliminar-movilizacion-button"
      onClick={onDelete}
      size="sm"
      variant="outline"
      className="gap-2 border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white font-['IBM_Plex_Sans'] transition-colors"
    >
      <Trash size={16} weight="bold" /> Eliminar
    </Button>
  </div>
);

const ACTION_TITLES = {
  en_espera_asignacion: 'Aprobar Movilización',
  rechazado: 'Rechazar Movilización',
};

const ACTION_DESCRIPTIONS = {
  en_espera_asignacion: 'Al aprobar, la movilización quedará en espera de asignación de vehículo y conductor.',
  rechazado: 'Indica el motivo del rechazo de la movilización.',
};

const AdminStateDialog = ({ open, onOpenChange, action, onSubmit, loading, comentario, setComentario }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md bg-white border border-[#E9ECEF]">
      <DialogHeader>
        <DialogTitle className="text-2xl font-black font-['Chivo'] tracking-tight text-[#0A0A0A]">
          {ACTION_TITLES[action]}
        </DialogTitle>
        <DialogDescription className="text-[#495057] font-['IBM_Plex_Sans']">
          {ACTION_DESCRIPTIONS[action]}
        </DialogDescription>
      </DialogHeader>

      <form data-testid="admin-action-form" onSubmit={onSubmit} className="space-y-4 mt-4">
        <div className="space-y-2">
          <Label htmlFor="comentario" className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">
            Comentario {action === 'rechazado' ? '*' : '(opcional)'}
          </Label>
          <Textarea
            id="comentario"
            data-testid="comentario-input"
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            placeholder="Agrega observaciones o motivos..."
            className="border-[#E9ECEF] focus:border-[#0891B2] font-['IBM_Plex_Sans'] min-h-[100px]"
            required={action === 'rechazado'}
          />
        </div>

        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1 border-[#E9ECEF] hover:bg-[#F8F9FA] font-['IBM_Plex_Sans']"
          >
            Cancelar
          </Button>
          <Button
            data-testid="confirm-action-button"
            type="submit"
            disabled={loading}
            className="flex-1 font-['IBM_Plex_Sans'] text-white transition-colors"
            style={{ backgroundColor: getActionColor(action) }}
          >
            {loading ? 'Procesando...' : 'Confirmar'}
          </Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);

const AdminAsignacionDialog = ({
  open, onOpenChange, movilizacion, onSubmit, loading, formData, setFormData, fleetProps,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md bg-white border border-[#E9ECEF]">
      <DialogHeader>
        <DialogTitle className="text-2xl font-black font-['Chivo'] tracking-tight text-[#0A0A0A]">
          {movilizacion.estado === 'asignado' ? 'Editar Asignación' : 'Asignar Recursos'}
        </DialogTitle>
        <DialogDescription className="text-[#495057] font-['IBM_Plex_Sans']">
          Registra vehículo, conductor y teléfono de contacto. Al completar todos los campos la movilización pasará a estado <strong>Asignado</strong>.
        </DialogDescription>
      </DialogHeader>

      <form data-testid="admin-asignacion-form" onSubmit={onSubmit} className="space-y-4 mt-4">
        <FleetSelectFields
          formData={formData}
          setFormData={setFormData}
          telefonoRequired
          {...fleetProps}
        />

        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1 border-[#E9ECEF] hover:bg-[#F8F9FA] font-['IBM_Plex_Sans']"
          >
            Cancelar
          </Button>
          <Button
            data-testid="admin-asignacion-confirm-button"
            type="submit"
            disabled={loading}
            className="flex-1 bg-[#008630] hover:bg-[#006624] text-white font-['IBM_Plex_Sans'] transition-colors"
          >
            {loading ? 'Guardando...' : 'Guardar Asignación'}
          </Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);

export const AdminMovilizacionCard = ({ movilizacion, onUpdate }) => {
  const [stateDialogOpen, setStateDialogOpen] = useState(false);
  const [asignacionDialogOpen, setAsignacionDialogOpen] = useState(false);
  const [cierreAction, setCierreAction] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [action, setAction] = useState(null);
  const [comentario, setComentario] = useState(movilizacion.comentario_admin || '');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    vehiculo: movilizacion.vehiculo || '',
    conductor: movilizacion.conductor || '',
    telefono_conductor: movilizacion.telefono_conductor || '',
    vehiculo_id: movilizacion.vehiculo_id || '',
    conductor_id: movilizacion.conductor_id || '',
  });

  const { vehiculos: vehiculosOpts, conductores: conductoresOpts } = useFleetOptions({
    enabled: asignacionDialogOpen,
    fechaInicio: movilizacion.fecha_inicio,
    fechaFin: movilizacion.fecha_fin,
    excludeMovId: movilizacion.id,
  });

  const onVehiculoSelect = buildVehiculoHandler(vehiculosOpts, setFormData);
  const onConductorSelect = buildConductorHandler(conductoresOpts, setFormData);

  const fleetProps = useMemo(
    () => ({ vehiculosOpts, conductoresOpts, onVehiculoSelect, onConductorSelect }),
    [vehiculosOpts, conductoresOpts, onVehiculoSelect, onConductorSelect]
  );

  const openStateDialog = (actionType) => {
    setAction(actionType);
    setComentario(movilizacion.comentario_admin || '');
    setStateDialogOpen(true);
  };

  const openAsignacionDialog = () => {
    setFormData({
      vehiculo: movilizacion.vehiculo || '',
      conductor: movilizacion.conductor || '',
      telefono_conductor: movilizacion.telefono_conductor || '',
      vehiculo_id: movilizacion.vehiculo_id || '',
      conductor_id: movilizacion.conductor_id || '',
    });
    setAsignacionDialogOpen(true);
  };

  const handleStateSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.patch(
        `${API_URL}/api/movilizaciones/${movilizacion.id}/estado`,
        { estado: action, comentario_admin: comentario },
        { withCredentials: true }
      );
      const successMsg = action === 'en_espera_asignacion'
        ? 'Movilización aprobada. Ahora en espera de asignación.'
        : `Movilización ${action} exitosamente`;
      toast.success(successMsg);
      setStateDialogOpen(false);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await axios.delete(
        `${API_URL}/api/movilizaciones/${movilizacion.id}`,
        { withCredentials: true }
      );
      toast.success('Movilización eliminada');
      setDeleteConfirm(false);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAsignacionSubmit = async (e) => {    e.preventDefault();
    setLoading(true);    try {
      const { data } = await axios.patch(
        `${API_URL}/api/movilizaciones/${movilizacion.id}/asignacion`,
        {
          vehiculo: formData.vehiculo,
          conductor: formData.conductor,
          telefono_conductor: formData.telefono_conductor,
          vehiculo_id: formData.vehiculo_id || null,
          conductor_id: formData.conductor_id || null,
        },
        { withCredentials: true }
      );
      const successMsg = data.estado === 'asignado'
        ? 'Recursos asignados. Movilización pasó a estado Asignado.'
        : 'Asignación actualizada';
      toast.success(successMsg);
      setAsignacionDialogOpen(false);
      onUpdate();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        data-testid={`admin-movilizacion-card-${movilizacion.id}`}
        className="bg-white border border-[#E9ECEF] p-6 hover:border-[#0A0A0A] transition-all"
      >
        <div className="flex items-start justify-between mb-4 gap-4">
          <MovilizacionStatusHeader movilizacion={movilizacion} />
          <div className="flex flex-wrap gap-2 justify-end">
            <AdminActionButtons
              movilizacion={movilizacion}
              onOpen={openStateDialog}
              onOpenAsignacion={openAsignacionDialog}
              onDelete={() => setDeleteConfirm(true)}
            />
            <CierreButtons movilizacion={movilizacion} onOpen={setCierreAction} testIdPrefix="admin" />
          </div>
        </div>

        <MovilizacionInfoGrid movilizacion={movilizacion} />
        <AsignacionDisplay movilizacion={movilizacion} showTelefonoSeparate title="Asignación de Transporte" />

        {movilizacion.comentario_admin && (
          <div className="mt-4 pt-4 border-t border-[#E9ECEF]">
            <p className="text-xs text-[#6C757D] font-['IBM_Plex_Sans'] mb-1">Comentario</p>
            <p className="text-sm font-['IBM_Plex_Sans'] text-[#0A0A0A]">{movilizacion.comentario_admin}</p>
          </div>
        )}

        {movilizacion.comentario_cierre && (
          <div className="mt-4 pt-4 border-t border-[#E9ECEF]">
            <p className="text-xs text-[#6C757D] font-['IBM_Plex_Sans'] mb-1">
              Cierre {movilizacion.cerrado_por ? `por ${movilizacion.cerrado_por}` : ''}
            </p>
            <p className="text-sm font-['IBM_Plex_Sans'] text-[#0A0A0A]">{movilizacion.comentario_cierre}</p>
          </div>
        )}
      </div>

      <AdminStateDialog
        open={stateDialogOpen}
        onOpenChange={setStateDialogOpen}
        action={action}
        onSubmit={handleStateSubmit}
        loading={loading}
        comentario={comentario}
        setComentario={setComentario}
      />

      <AdminAsignacionDialog
        open={asignacionDialogOpen}
        onOpenChange={setAsignacionDialogOpen}
        movilizacion={movilizacion}
        onSubmit={handleAsignacionSubmit}
        loading={loading}
        formData={formData}
        setFormData={setFormData}
        fleetProps={fleetProps}
      />

      <CierreDialog
        movilizacion={movilizacion}
        cierreAction={cierreAction}
        open={!!cierreAction}
        onOpenChange={(v) => !v && setCierreAction(null)}
        onSuccess={onUpdate}
      />

      <Dialog open={deleteConfirm} onOpenChange={setDeleteConfirm}>
        <DialogContent className="max-w-md bg-white border border-[#E9ECEF]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black font-['Chivo'] tracking-tight text-[#0A0A0A]">
              Eliminar Movilización
            </DialogTitle>
            <DialogDescription className="text-[#495057] font-['IBM_Plex_Sans']">
              Esta acción es permanente y no se puede deshacer. La movilización será eliminada por completo.
            </DialogDescription>
          </DialogHeader>
          <div className="p-3 bg-[#FF3B30]/10 border border-[#FF3B30] mt-4">
            <p className="text-sm font-['IBM_Plex_Sans'] text-[#0A0A0A]">
              <strong>ID:</strong> {movilizacion.id ? movilizacion.id.slice(0, 8) : 'N/A'} · <strong>Estado:</strong> {movilizacion.estado}
            </p>
          </div>
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteConfirm(false)}
              className="flex-1 border-[#E9ECEF] hover:bg-[#F8F9FA] font-['IBM_Plex_Sans']"
            >
              Cancelar
            </Button>
            <Button
              data-testid="confirm-delete-movilizacion-button"
              onClick={handleDelete}
              disabled={loading}
              className="flex-1 bg-[#FF3B30] hover:bg-[#c22e26] text-white font-['IBM_Plex_Sans']"
            >
              {loading ? 'Eliminando...' : 'Eliminar Definitivamente'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
